// Copyright ©️ 2026 eVolve MEP, LLC

import type { FacilityId, TaskTimeEntry } from '@evolvemep/api-ts';

import { GridMenu } from 'components/Mantine/GridMenu';
import { FormModal } from 'components/Mantine/Modals/FormModal';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { CreateEditTimeEntryForm } from 'modules/Shop/TimeTracking/CreateTimeEntryModal';

type Props = {
  entry: TaskTimeEntry;
  facilityId: FacilityId;
  onEntryUpdated: (updatedEntry: TaskTimeEntry) => void;
  onDeleteEntry: (entry: TaskTimeEntry) => void;
};

export const TimeEntryMenu = ({ facilityId, entry, onEntryUpdated, onDeleteEntry }: Props) => {
  const navigate = useEvolveNavigate('Time Tracking');
  return (
    <GridMenu
      options={['Edit', { value: 'Duplicate', disabled: entry.user.isDeleted }, 'Open task', 'Delete']}
      onMenuItemSelected={(menuItemSelected) => {
        if (menuItemSelected === 'Open task') {
          void navigate(`/task-viewer/${entry.taskId}`);
        } else if (menuItemSelected === 'Delete') {
          onDeleteEntry(entry);
          return true;
        }
        return false;
      }}
    >
      {(menuItemSelected, resetMenu) => (
        <FormModal
          opened={menuItemSelected === 'Edit' || menuItemSelected === 'Duplicate'}
          onClose={resetMenu}
          title={`${menuItemSelected === 'Edit' ? 'Edit' : 'New'} time entry`}
          type="submit"
          confirmationText={menuItemSelected === 'Edit' ? 'Save' : 'Create'}
        >
          <CreateEditTimeEntryForm
            facilityId={facilityId}
            onTimeEntrySaved={onEntryUpdated}
            taskTimeEntryId={menuItemSelected === 'Edit' ? entry.taskTimeEntryId : undefined}
            defaultValue={{
              workCellTask: entry,
              workDate: entry.workDate,
              totalSeconds: entry.totalSeconds,
              user: entry.user,
            }}
          />
        </FormModal>
      )}
    </GridMenu>
  );
};
